/**
 * Turns a caption's resolved values into readable label/value rows, so the
 * Copilot window shows WHAT is going to air rather than only which caption is
 * selected.
 *
 * Only the block named after the caption is summarised. Layers are shared — the
 * resolved tree for gfx_PermScore carries the whole persistent clock/score
 * block (venue, branding, set style, both teams) — so walking it wholesale
 * buries the caption's own content under template furniture and shows the same
 * label twice for the two teams.
 *
 * A caption with no matching block shows nothing, which is honest: better than
 * confidently listing the wrong fields.
 *
 * Each row carries the path it was read from, so the window can write a
 * corrected value back through the library's own setRootFormData.
 */

const MAX_ROWS = 8;

// Template fields carry hungarian prefixes (strScore, iRedCardTabs, bIsHome).
const humanise = (key) =>
  key
    .replace(/^(str|int|num|flt|[ibfn])(?=[A-Z])/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());

/*
 * Asset references are the graphic's own authoring paths — "PLP\2023_PLP_Style\
 * Flags\IRL.webp". The renderer bundles its images per graphic with content
 * hashes (…/gfx_plp-goalrush/v1.0/_nuxt/HUL_H.D3uKAFsX.png) and never requests
 * these paths over HTTP, so there is nothing to preview and nothing an operator
 * can read. They also crowd out the fields that matter.
 */
const isAssetPath = (v) =>
  typeof v === 'string' &&
  (v.includes('\\') || /\.(webp|png|jpe?g|gif|svg|mp4|webm|mov)$/i.test(v.trim()));

const isContent = (v) =>
  ((typeof v === 'string' && v.trim() !== '') || (typeof v === 'number' && Number.isFinite(v))) &&
  !isAssetPath(v);

const isPlainObject = (v) => Boolean(v) && typeof v === 'object' && !Array.isArray(v);

/**
 * Depth-first search for the block named `name`, with the route taken to reach
 * it. The route is what makes a row writable: without it a value can be shown
 * but never put back.
 *
 * @returns {{block: object, trail: string[]} | null}
 */
const findBlock = (node, name, trail = [], depth = 0) => {
  if (!isPlainObject(node) || depth > 6) return null;

  const hit = Object.keys(node).find((k) => k.toLowerCase() === name.toLowerCase());
  if (hit && isPlainObject(node[hit])) return { block: node[hit], trail: [...trail, hit] };

  return Object.entries(node).reduce(
    (found, [key, child]) => found || findBlock(child, name, [...trail, key], depth + 1),
    null,
  );
};

/**
 * @param {object} rootFormData resolved caption data
 * @param {string} captionName the selected caption, e.g. "GoalScorer"
 * @returns {{label: string, value: string}[]}
 */
export const summariseCaptionData = (rootFormData, captionName) => {
  if (!isPlainObject(rootFormData) || !captionName) return [];

  // Variants share one block: Cards_PrevCommitted lives under Cards.
  const block =
    findBlock(rootFormData, captionName) ||
    findBlock(rootFormData, String(captionName).split('_')[0]);

  if (!block) return [];

  const rows = [];
  const seen = new Set();

  const walk = (node, trail) => {
    if (rows.length >= MAX_ROWS || !isPlainObject(node)) return;

    Object.entries(node).forEach(([key, value]) => {
      if (rows.length >= MAX_ROWS || key.startsWith('$')) return;

      if (isContent(value)) {
        const row = {
          label: humanise(key),
          value: String(value),
          path: [...trail, key].join('.'),
        };
        const id = `${row.label}\u0000${row.value}`;
        if (!seen.has(id)) {
          seen.add(id);
          rows.push(row);
        }
        return;
      }

      if (isPlainObject(value)) walk(value, [...trail, key]);
    });
  };

  walk(block.block, block.trail);
  return rows;
};
